import React, { useEffect, useMemo, useState } from "react";
import { BellRing, Laptop, Power, RefreshCcw, Shield, Smartphone } from "lucide-react";
import { Button, Card, DataTable, StatusBadge } from "./ui";
import { formatDateTime } from "../utils/session";

function summarizeUserAgent(userAgent) {
  const ua = String(userAgent || "");
  const browser =
    /firefox/i.test(ua) ? "Firefox"
      : /edg/i.test(ua) ? "Edge"
        : /opr|opera/i.test(ua) ? "Opera"
          : /chrome|crios/i.test(ua) ? "Chrome"
            : /safari/i.test(ua) ? "Safari"
              : "Navegador";

  const platform =
    /windows/i.test(ua) ? "Windows"
      : /android/i.test(ua) ? "Android"
        : /iphone|ipad|ipod/i.test(ua) ? "iOS"
          : /mac os x|macintosh/i.test(ua) ? "macOS"
            : /linux/i.test(ua) ? "Linux"
              : "Plataforma";

  const formFactor = /mobile|android|iphone|ipod/i.test(ua) ? "mobile" : "desktop";
  return {
    label: `${browser} • ${platform}`,
    formFactor,
    raw: ua || "User-Agent nao informado",
  };
}

function CurrentDevicePanel({
  pushState,
  pushBusy = false,
  pushResetBusy = false,
  pushTestBusy = false,
  pushLastError = "",
  onEnablePush,
  onDisablePush,
  onResetPush,
  onTestPush,
}) {
  if (!pushState) {
    return null;
  }

  const pushSupported = Boolean(pushState.supported);
  const pushSubscribed = Boolean(pushState.subscribed);
  const permission = pushState.permission;

  return (
    <div className="mb-4 rounded-2xl border border-border/70 bg-background/70 p-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="space-y-1">
          <p className="text-sm font-semibold text-foreground">Este navegador</p>
          <p className="text-xs text-muted-foreground">
            Controle o Web Push deste dispositivo. Dispositivos antigos podem ser desativados logo abaixo.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={pushSubscribed ? onDisablePush : onEnablePush} disabled={!pushSupported || pushBusy}>
            <Smartphone className="h-4 w-4" />
            {pushBusy ? "Processando..." : pushSubscribed ? "Desativar neste navegador" : "Ativar neste navegador"}
          </Button>
          <Button variant="outline" size="sm" onClick={onResetPush} disabled={pushResetBusy || pushBusy}>
            <RefreshCcw className="h-4 w-4" />
            {pushResetBusy ? "Resetando..." : "Resetar push"}
          </Button>
          <Button variant="outline" size="sm" onClick={onTestPush} disabled={!pushSubscribed || pushTestBusy || pushBusy || pushResetBusy}>
            <BellRing className="h-4 w-4" />
            {pushTestBusy ? "Testando..." : "Testar push"}
          </Button>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-2">
        <StatusBadge
          status={permission === "granted" ? "Permissao permitida" : permission === "denied" ? "Permissao bloqueada" : "Permissao pendente"}
          variant={permission === "granted" ? "success" : permission === "denied" ? "danger" : "default"}
        />
        <StatusBadge
          status={pushSubscribed ? "Push ativo" : "Push desativado"}
          variant={pushSubscribed ? "success" : "default"}
        />
        <StatusBadge
          status={pushState?.backendStatus?.currentEndpointRegistered ? "Registrado no backend" : "Não registrado"}
          variant={pushState?.backendStatus?.currentEndpointRegistered ? "info" : "default"}
        />
      </div>

      {pushLastError ? (
        <p className="mt-3 text-xs font-medium text-destructive">{pushLastError}</p>
      ) : null}
    </div>
  );
}

export default function WebPushDevicesSection({
  title = "Dispositivos com Web Push",
  description,
  devices = [],
  loading = false,
  onRefresh,
  onDisableDevice,
  disableDeviceBusyId = null,
  currentEndpointSuffix = null,
  emptyState = "Nenhum dispositivo com Web Push registrado",
  currentPushControls = null,
}) {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const tableData = useMemo(
    () =>
      devices.map((device) => ({
        ...device,
        summary: summarizeUserAgent(device.userAgent),
        isCurrent: Boolean(currentEndpointSuffix) && device.endpointSuffix === currentEndpointSuffix,
      })),
    [devices, currentEndpointSuffix]
  );

  const totalPages = Math.max(1, Math.ceil(tableData.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, Math.max(totalPages - 1, 0)));
  }, [totalPages]);

  const pagedData = useMemo(() => {
    const start = page * pageSize;
    return tableData.slice(start, start + pageSize);
  }, [page, pageSize, tableData]);

  const pagination = useMemo(
    () => ({
      page,
      size: pageSize,
      totalPages,
      totalElements: tableData.length,
    }),
    [page, pageSize, tableData.length, totalPages]
  );

  const columns = useMemo(
    () => [
      {
        key: "enabled",
        header: "Status",
        mobilePrimary: true,
        mobileOrder: 0,
        render: (value, row) => (
          <div className="flex flex-wrap items-center gap-2">
            <StatusBadge status={value ? "Ativo" : "Desativado"} variant={value ? "success" : "default"} />
            {row.isCurrent ? <StatusBadge status="Este navegador" variant="info" /> : null}
          </div>
        ),
      },
      {
        key: "summary",
        header: "Dispositivo",
        mobileOrder: 1,
        render: (value, row) => (
          <div className="space-y-1">
            <p className="flex items-center gap-2 font-medium text-foreground">
              {row.summary.formFactor === "mobile" ? <Smartphone className="h-4 w-4" /> : <Laptop className="h-4 w-4" />}
              {row.summary.label}
            </p>
            <p className="text-xs text-muted-foreground">{row.summary.raw}</p>
          </div>
        ),
      },
      {
        key: "endpointSuffix",
        header: "Endpoint",
        mobileOrder: 2,
        render: (value) => <span className="font-mono text-xs">{value || "Não informado"}</span>,
      },
      {
        key: "lastSuccessfulPushAt",
        header: "Ultimo push",
        mobileOrder: 3,
        render: (value) => formatDateTime(value),
      },
      {
        key: "updatedAt",
        header: "Atualizado em",
        mobileOrder: 4,
        render: (value) => formatDateTime(value),
      },
    ],
    []
  );

  const actions = useMemo(
    () => [
      {
        icon: Power,
        title: "Desativar",
        onClick: onDisableDevice,
        hidden: (row) => !row.enabled || typeof onDisableDevice !== "function",
        disabled: (row) => disableDeviceBusyId != null && row.id === disableDeviceBusyId,
      },
    ],
    [disableDeviceBusyId, onDisableDevice]
  );

  return (
    <Card title={title}>
      {description || onRefresh ? (
        <div className="mb-4 flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
          <p>{description || "Gerencie quais navegadores e dispositivos ainda podem receber Web Push."}</p>
          {onRefresh ? (
            <Button variant="outline" size="sm" onClick={onRefresh}>
              <RefreshCcw className="h-4 w-4" />
              Atualizar dispositivos
            </Button>
          ) : null}
        </div>
      ) : null}

      {currentPushControls ? <CurrentDevicePanel currentEndpointSuffix={currentEndpointSuffix} {...currentPushControls} /> : null}

      <div className="rounded-2xl border border-border/60 bg-background/60 p-4">
        <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Shield className="h-4 w-4" />
          Dispositivos registrados
        </div>

        <DataTable
          columns={columns}
          data={pagedData}
          actions={actions}
          loading={loading}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={(nextSize) => {
            setPage(0);
            setPageSize(nextSize);
          }}
          enablePagination={tableData.length > pageSize}
          emptyState={emptyState}
        />
      </div>
    </Card>
  );
}
